import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, LogIn, LogOut, Package, User, Truck, Wifi, WifiOff, Check } from "lucide-react";

// Guard PWA (/gate) — mobile-first, offline-ready visitor entry log.
// Big buttons, minimal fields, bilingual (Hindi + English). Queues to IndexedDB
// when offline (best-effort using localStorage as fallback), flushes on reconnect.
const QUEUE_KEY = "hostro_gate_queue_v1";

async function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
}
function saveQueue(q) { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); }

export default function GuardPWA() {
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [tab, setTab] = useState("visitor");
  const [form, setForm] = useState({ visitor_name: "", visitor_phone: "", purpose: "", whom_to_meet: "", vehicle_no: "" });
  const [delivery, setDelivery] = useState({ visitor_name: "Delivery", visitor_phone: "", purpose: "Delivery", company: "Swiggy" });
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const properties = async () => {
    try {
      const { data } = await api.get(`/properties?owner_id=${user?.id || ""}`);
      if (data?.length) setProperty(data[0]);
    } catch { /* */ }
  };
  const fetchLogs = async () => {
    if (!property) return;
    try { const { data } = await api.get(`/pms/gate/logs?property_id=${property.id}`); setLogs(data.slice(0, 20)); }
    catch { /* */ }
  };

  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => { properties(); loadQueue().then(setQueue); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => { if (property) fetchLogs(); /* eslint-disable-next-line */ }, [property]);

  useEffect(() => {
    // Flush queue when online
    if (online && queue.length > 0) {
      (async () => {
        const remaining = [];
        for (const item of queue) {
          try { await api.post("/pms/gate/entry", item); }
          catch { remaining.push(item); }
        }
        setQueue(remaining); saveQueue(remaining);
        if (queue.length - remaining.length > 0) toast.success(`Synced ${queue.length - remaining.length} entries`);
        fetchLogs();
      })();
    }
    // eslint-disable-next-line
  }, [online]);

  const submit = async (payload) => {
    if (!property) { toast.error("Select property first"); return; }
    setBusy(true);
    const body = { ...payload, property_id: property.id };
    if (!online) {
      const q = [...queue, body]; setQueue(q); saveQueue(q);
      toast.success("Queued offline — will sync when online");
      setBusy(false);
      return true;
    }
    try {
      await api.post("/pms/gate/entry", body);
      toast.success("Entry logged");
      fetchLogs();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); return false; }
    finally { setBusy(false); }
  };

  const submitVisitor = async (e) => {
    e.preventDefault();
    const ok = await submit({ ...form, is_delivery: false });
    if (ok) setForm({ visitor_name: "", visitor_phone: "", purpose: "", whom_to_meet: "", vehicle_no: "" });
  };
  const submitDelivery = async () => {
    const ok = await submit({ ...delivery, is_delivery: true });
    if (ok) setDelivery({ ...delivery, visitor_phone: "" });
  };
  const markExit = async (id) => {
    try { await api.put(`/pms/gate/${id}/exit`); toast.success("Exit marked"); fetchLogs(); }
    catch { toast.error("Failed"); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white text-lg focus:border-[#34C97B] focus:ring-2 focus:ring-[#34C97B]/30 outline-none";

  return (
    <div className="min-h-screen text-white" style={{background:"#0D1B2A", fontFamily:"Inter, ui-sans-serif, system-ui"}} data-testid="guard-pwa">
      {/* Header */}
      <div className="sticky top-0 z-10 px-4 py-3 border-b border-white/10 backdrop-blur bg-[#0D1B2A]/80">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-[#34C97B]" size={22}/>
            <div>
              <div className="text-sm font-bold">Guard · Hostro Gate</div>
              <div className="text-[10px] text-white/50">{property?.title || "Loading..."}</div>
            </div>
          </div>
          <div className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${online?"bg-[#34C97B]/15 text-[#34C97B]":"bg-rose-500/15 text-rose-300"}`} data-testid="online-badge">
            {online ? <Wifi size={12}/> : <WifiOff size={12}/>}{online?"Online":"Offline"}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* Tab switch */}
        <div className="grid grid-cols-2 gap-1 p-1 bg-white/5 border border-white/10 rounded-xl mb-5">
          <button onClick={()=>setTab("visitor")} className={`inline-flex items-center justify-center gap-2 py-3 rounded-lg font-semibold ${tab==="visitor"?"bg-[#34C97B] text-white shadow":"text-white/60"}`} data-testid="tab-visitor"><User size={16}/> Visitor</button>
          <button onClick={()=>setTab("delivery")} className={`inline-flex items-center justify-center gap-2 py-3 rounded-lg font-semibold ${tab==="delivery"?"bg-[#34C97B] text-white shadow":"text-white/60"}`} data-testid="tab-delivery"><Package size={16}/> Delivery</button>
        </div>

        {tab === "visitor" ? (
          <form onSubmit={submitVisitor} className="space-y-3">
            <input required className={inputCls} placeholder="विज़िटर का नाम / Visitor name" value={form.visitor_name} onChange={(e)=>setForm({...form, visitor_name:e.target.value})} data-testid="v-name"/>
            <input required className={inputCls} placeholder="फ़ोन / Phone" value={form.visitor_phone} onChange={(e)=>setForm({...form, visitor_phone:e.target.value})} data-testid="v-phone"/>
            <input required className={inputCls} placeholder="किससे मिलना है / Whom to meet" value={form.whom_to_meet} onChange={(e)=>setForm({...form, whom_to_meet:e.target.value})} data-testid="v-meet"/>
            <input className={inputCls} placeholder="काम / Purpose" value={form.purpose} onChange={(e)=>setForm({...form, purpose:e.target.value})} data-testid="v-purpose"/>
            <input className={inputCls} placeholder="वाहन / Vehicle no. (optional)" value={form.vehicle_no} onChange={(e)=>setForm({...form, vehicle_no:e.target.value})} data-testid="v-vehicle"/>
            <button disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-bold py-4 rounded-xl text-lg transition" data-testid="log-entry-btn">
              <LogIn size={20}/> Log Entry
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              {["Swiggy","Zomato","Amazon","Other"].map(c => (
                <button key={c} onClick={()=>setDelivery({...delivery, company:c})} className={`py-3 rounded-xl text-xs font-bold ${delivery.company===c?"bg-[#34C97B] text-white":"bg-white/5 text-white/70 border border-white/10"}`} data-testid={`co-${c}`}>{c}</button>
              ))}
            </div>
            <input className={inputCls} placeholder="Delivery boy phone (optional)" value={delivery.visitor_phone} onChange={(e)=>setDelivery({...delivery, visitor_phone:e.target.value})} data-testid="d-phone"/>
            <input className={inputCls} placeholder="Room / Flat" value={delivery.whom_to_meet||""} onChange={(e)=>setDelivery({...delivery, whom_to_meet:e.target.value})} data-testid="d-room"/>
            <button onClick={submitDelivery} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-bold py-4 rounded-xl text-lg transition" data-testid="log-delivery-btn">
              <Truck size={20}/> Log Delivery
            </button>
          </div>
        )}

        {queue.length > 0 && (
          <div className="mt-5 rounded-xl p-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm" data-testid="queue-banner">
            {queue.length} entries queued — will sync when online.
          </div>
        )}

        {/* Recent logs */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Recent Entries</div>
          <div className="space-y-2">
            {logs.length === 0 && <div className="text-sm text-white/40 text-center py-6">No entries yet</div>}
            {logs.map(l => (
              <div key={l.id} className="rounded-xl p-3 bg-white/5 border border-white/10 flex items-center gap-3" data-testid={`log-${l.id}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center ${l.is_delivery?"bg-blue-500/15 text-blue-300":"bg-[#34C97B]/15 text-[#34C97B]"}`}>
                  {l.is_delivery ? <Package size={14}/> : <User size={14}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{l.visitor_name} {l.company?`· ${l.company}`:""}</div>
                  <div className="text-[11px] text-white/50 truncate">{new Date(l.entry_at).toLocaleTimeString()} · {l.whom_to_meet||l.purpose||"—"}</div>
                </div>
                {l.exit_at ? (
                  <span className="text-[10px] text-white/40">Exited</span>
                ) : (
                  <button onClick={()=>markExit(l.id)} className="text-xs px-2 py-1 rounded-md bg-white/5 border border-white/15 text-white/70 hover:border-[#34C97B]/50 hover:text-[#34C97B] transition" data-testid={`exit-${l.id}`}>
                    <LogOut size={12} className="inline"/> Exit
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
